import { PlusOutlined } from "@ant-design/icons";
import {
  Button,
  Form,
  Input,
  Modal,
  Row,
  Space,
  Spin,
  Upload,
  message,
} from "antd";
import { useMutation } from "react-query";
import { TCategoryForm } from "../../types/Category/TCategoryForm";
import categoryRepo from "../../API/LayoutApi/category.repo";
import ProviderSelect from "../Providers/ProviderSelect";
import { omit } from "lodash";
import { TCategory } from "../../types/Category/TCategory";

function CategoryForm({
  id,
  open,
  initialValues,
  setOpen,
  onSubmit,
}: {
  id?: string;
  open?: boolean;
  initialValues?: TCategory;
  setOpen?: (value: boolean) => void;
  onSubmit?: (formValues: TCategoryForm) => void;
}) {
  const { isLoading: loadingCreate, mutateAsync: mutateCreate } = useMutation({
    mutationFn: (formValues: TCategoryForm) =>
      categoryRepo.addCategory(formValues),
    onError: (error: string) => {
      message.error(error);
    },
  });
  const { isLoading: loadingUpdate, mutateAsync: mutateUpdate } = useMutation({
    mutationFn: ({
      id,
      formValues,
    }: {
      id: string;
      formValues: TCategoryForm;
    }) => categoryRepo.editCategory(id, formValues),
    onError: (error: string) => {
      message.error(error);
    },
  });
  const { isLoading: loadingUpload, mutate: mutateUpload } = useMutation({
    mutationFn: ({ id, file }: { id: string; file: File }) =>
      categoryRepo.fileUpload(id, file),
    onError: (error: string) => {
      message.error(error);
    },
  });

  function normFile(e: any) {
    if (Array.isArray(e)) {
      return e;
    }
    return e?.fileList;
  }

  async function onFinishForm(values: any) {
    const formValues: TCategoryForm = {
      name: values.name,
      icon: values.icon,
      provider_ids: values.provider_ids,
    };
    if (values.file) {
      formValues.file = values.file[0].originFileObj;
    }
    try {
      const mutatedData = !id
        ? await mutateCreate(omit(formValues, ["file"]))
        : await mutateUpdate({ id, formValues: omit(formValues, ["file"]) });
      if (formValues.file) {
        const uploadData: { id: string; file: File } = {
          id: "",
          file: formValues.file,
        };
        if (mutatedData) {
          uploadData.id = mutatedData.id;
        } else if (id) {
          uploadData.id = id;
        }
        await mutateUpload(uploadData);
      }
      onSubmit && onSubmit(formValues);
      setOpen && setOpen(false);
    } catch (err) {}
  }

  return (
    <Modal
      open={open}
      onCancel={() => setOpen && setOpen(false)}
      title={id ? "Edit Category" : "Add Category"}
      footer={null}
      maskClosable={false}
    >
      <Spin spinning={loadingCreate || loadingUpdate || loadingUpload}>
        <Form
          layout="vertical"
          initialValues={initialValues}
          onFinish={onFinishForm}
        >
          <Form.Item
            label="Category name"
            name="name"
            rules={[
              { required: true, message: "Please input your category name!" },
            ]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            label="Icon"
            name="icon"
            rules={[
              { required: true, message: "Please input your icon name!" },
            ]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            label="Category logo"
            name="file"
            valuePropName="fileList"
            getValueFromEvent={normFile}
          >
            <Upload
              listType="picture-card"
              name="file"
              maxCount={1}
              beforeUpload={() => false}
            >
              <div>
                <PlusOutlined />
                <div style={{ marginTop: 8 }}>Upload</div>
              </div>
            </Upload>
          </Form.Item>
          <Form.Item label="Providers" name="provider_ids">
            <ProviderSelect multiple />
          </Form.Item>
          <Row justify="end">
            <Space>
              <Button onClick={() => setOpen && setOpen(false)}>Cancel</Button>
              <Button type="primary" htmlType="submit">
                Save
              </Button>
            </Space>
          </Row>
        </Form>
      </Spin>
    </Modal>
  );
}

export default CategoryForm;
